import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '@core/environment/environment.service';
import {Observable} from 'rxjs';
import {ConsentRequestModel} from '@core/modules/rest/consent/request/consent-request.model';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {SubmitConsentRequestModel} from '@core/modules/rest/consent/request/submit-consent-request.model';
import {GetMoreUnitsConsentRequestModel} from '@core/modules/rest/consent/request/get-more-units-consent-request.model';
import {CommentRequestModel} from '@core/modules/rest/consent/request/comment-request.model';
import {CommentBasicModel} from '@core/modules/rest/consent/response/comment-basic.model';
import {EditCommentRequestModel} from '@core/modules/rest/consent/request/edit-comment-request.model';
import {OutlineChapterWithMarkerBasicModel} from '@core/modules/rest/consent/response/outline-chapter-with-marker-basic.model';
import {ConsentidQuestionAndMarkerRequestModel} from '@core/modules/rest/consent/request/consentid-question-and-marker-request.model';
import {SubmittedConsentModel} from '@core/modules/rest/consent/response/submitted-consent.model';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {LanguageEnum} from '@core/enums/language.enum';
import {ConsentBasicModel} from '@core/modules/rest/consent/response/consent-basic.model';
import {SubmitConsentOverviewRequestModel} from '@core/modules/rest/consent/request/submit-consent-overview-request.model';
import {ResumeConsentModel} from '@core/modules/rest/consent/response/resume-consent.model';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';
import {SingleCommentRequestModel} from '@core/modules/rest/consent/request/single-comment-request.model';
import {PollyDataModel} from '@core/modules/rest/questions/response/polly-data.model';
import {SubmittedConsentSimpleModel} from '@core/modules/rest/consent/response/submitted-consent-simple.model';
import {CommentAnswerRequestModel} from '@core/modules/rest/consent/request/comment-answer-request.model';
import {NoteRequestModel} from '@core/modules/rest/consent/request/note-request.model';
import {NoteModel} from '@core/modules/rest/consent/response/note.model';
import {OutlineChapterUnitWithMarkerBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-with-marker-basic.model';
import {SubmittedInitialConsentModel} from '@core/modules/rest/consent/response/submitted-initial-consent.model';
import {ConsentStatusEnum} from '@core/enums/consent-status.enum';
import {SearchDataModel} from '@shared/model/search-data.model';
import {TreatmentModel} from '@core/modules/rest/consent/request/treatment.model';

@Injectable()
export class ConsentRestService {

    private controllerUrl = this.environmentService.serverApiUrl + '/secured/consent';

    constructor(private httpClient: HttpClient, private environmentService: EnvironmentService) {
    }

    getConsent(consent: ConsentRequestModel): Observable<OutlineChapterUnitWithMarkerBasicModel> {
        return this.httpClient.post<OutlineChapterUnitWithMarkerBasicModel>(this.controllerUrl.concat('/').concat(consent.indicationId).concat('/outline'), consent);
    }

    getConsentDuration(consent: ConsentRequestModel): Observable<number> {
        return this.httpClient.post<number>(this.controllerUrl.concat('/').concat(consent.indicationId).concat('/duration'), consent);
    }

    getMoreUnits(requestModel: GetMoreUnitsConsentRequestModel): Observable<OutlineUnitBasicModel[]> {
        return this.httpClient.post<OutlineUnitBasicModel[]>(this.controllerUrl.concat('/unitOrderId/').concat(String(requestModel.unitOrderId)).concat('/load-more'), requestModel);
    }

    submitInitialConsent(wizardContent: string): Observable<string> {
        return this.httpClient.post<string>(this.controllerUrl.concat('/submitInitial'), wizardContent);
    }

    submitInitialFromSettings(): Observable<SubmittedInitialConsentModel> {
        return this.httpClient.post<SubmittedInitialConsentModel>(this.controllerUrl.concat('/submitInitialFromSettings'), null);
    }

    submitConsent(request: SubmitConsentRequestModel): Observable<SubmittedConsentModel> {
        return this.httpClient.put<SubmittedConsentModel>(this.controllerUrl.concat('/submit'), request);
    }

    getAllSubmittedConsents(offset: number): Observable<SubmittedConsentSimpleModel[]> {
        return this.httpClient.get<SubmittedConsentSimpleModel[]>(this.controllerUrl.concat(`/consent-list/${offset}`));
    }

    getAllSubmittedConsentsFiltered(filter: SearchDataModel): Observable<SubmittedConsentSimpleModel[]> {
        return this.httpClient.post<SubmittedConsentSimpleModel[]>(this.controllerUrl.concat(`/consent-list/filter`), filter);
    }

    getMySubmittedConsents(): Observable<SubmittedConsentSimpleModel[]> {
        return this.httpClient.get<SubmittedConsentSimpleModel[]>(this.controllerUrl.concat('/my-consent-list'));
    }

    getSpecificSubmittedConsent(id: string): Observable<SubmittedConsentModel> {
        return this.httpClient.get<SubmittedConsentModel>(this.controllerUrl.concat('/review-consents/').concat(id));
    }

    submitComment(request: CommentRequestModel): Observable<CommentBasicModel> {
        return this.httpClient.post<CommentBasicModel>(this.controllerUrl.concat('/comment'), request);
    }

    getConsentComments(id: string): Observable<CommentBasicModel[]> {
        return this.httpClient.get<CommentBasicModel[]>(this.controllerUrl.concat('/comments/').concat(id));
    }

    submitNote(request: NoteRequestModel): Observable<NoteModel> {
        return this.httpClient.post<NoteModel>(this.controllerUrl.concat('/').concat(request.submittedConsentId).concat('/note'), request);
    }

    getConsentChapterNotes(submittedConsentId: string, chapterId: string): Observable<NoteModel[]> {
        let url = this.controllerUrl.concat('/').concat(submittedConsentId);
        url = chapterId ? url.concat('/chapter/').concat(chapterId) : url;
        url = url.concat('/notes');
        return this.httpClient.get<NoteModel[]>(url);
    }

    deleteComment(commentId: string): Observable<any> {
        return this.httpClient.delete(this.controllerUrl.concat('/delete-comment/').concat(commentId));
    }

    editComment(request: EditCommentRequestModel): Observable<CommentBasicModel> {
        return this.httpClient.put<CommentBasicModel>(this.controllerUrl.concat('/comment'), request);
    }

    exportToCsv(): any {
        return this.httpClient.get(this.controllerUrl.concat('/export'), {responseType: 'blob'});
    }

    saveQuestions(request: ConsentidQuestionAndMarkerRequestModel): any {
        return this.httpClient.put(this.controllerUrl.concat('/updateQuestionnaire'), request);
    }

    getUnitInLanguageStyle(unitId: string, indicationId: string, language: LanguageEnum, languageStyle: LanguageStyleEnum): Observable<OutlineUnitBasicModel> {
        return this.httpClient.get<OutlineUnitBasicModel>(this.controllerUrl.concat(`/units/${unitId}/indicationId/${indicationId}/language/${language}/languageStyle/${languageStyle}`));
    }

    getFullConsent(consentId: string, language: LanguageEnum): Observable<ConsentBasicModel> {
        return this.httpClient.get<ConsentBasicModel>(this.controllerUrl.concat(`/${consentId}/${language}`));
    }

    saveOverviewDuration(consentId: string, requestObject: SubmitConsentOverviewRequestModel): Observable<void> {
        return this.httpClient.post<void>(this.controllerUrl.concat(`/${consentId}/overviewDuration`), requestObject);
    }

    finishPatientEducation(consentId: string): Observable<void> {
        return this.httpClient.post<void>(this.controllerUrl.concat(`/${consentId}/finishEducation`), null);
    }

    getStartedConsent(language: LanguageEnum): Observable<ResumeConsentModel> {
        return this.httpClient.get<ResumeConsentModel>(this.controllerUrl.concat(`/resume-consent/${language}`));
    }

    markQuestionnaireAsStarted(consentId: string): any {
        return this.httpClient.get(this.controllerUrl.concat(`/${consentId}/mark-questionnaire-started`));
    }

    markOverviewAsStarted(consentId: string): any {
        return this.httpClient.get(this.controllerUrl.concat(`/${consentId}/mark-overview-started`));
    }

    getConsentForDetailView(consentId: any, language: LanguageEnum): Observable<OutlineChapterUnitBasicModel[]> {
        return this.httpClient.get<OutlineChapterUnitBasicModel[]>(this.controllerUrl.concat(`/${consentId}/details/${language}`));
    }

    saveSingleComment(request: SingleCommentRequestModel): any {
        return this.httpClient.post(this.controllerUrl.concat('/single-comment'), request);
    }

    getPollyDataForAnamnesis(consentId: string): Observable<any> {
        return this.httpClient.get<PollyDataModel>(this.controllerUrl.concat(`/${consentId}/polly-data`));
    }

    getSingleComment(consentId: string): any {
        return this.httpClient.get<CommentBasicModel>(this.controllerUrl.concat(`/${consentId}/single-comment`));
    }

    answerComment(request: CommentAnswerRequestModel): Observable<any> {
        return this.httpClient.put<CommentBasicModel>(this.controllerUrl.concat('/answer-comment'), request);
    }

    submitInitialBasicConsent(): Observable<string> {
        return this.httpClient.get<string>(this.controllerUrl.concat('/basic/createInitial'));
    }

    getBasicConsent(consentId: string): Observable<OutlineChapterWithMarkerBasicModel> {
        return this.httpClient.get<OutlineChapterWithMarkerBasicModel>(this.controllerUrl + `/basic/${consentId}/outline`);
    }

    markConsentAs(consentId: string, status: ConsentStatusEnum): Observable<void> {
        return this.httpClient.put<void>(this.controllerUrl.concat(`/${consentId}`).concat(`/${status}`), null);
    }

    assignTreatment(consentId: string, treatment: TreatmentModel): Observable<ConsentStatusEnum> {
        return this.httpClient.put<ConsentStatusEnum>(this.controllerUrl.concat(`/${consentId}/assign-treatment`), treatment);
    }

    getConsentTreatment(consentId: string): Observable<TreatmentModel> {
        return this.httpClient.get<TreatmentModel>(this.controllerUrl.concat(`/${consentId}/get-treatment`));
    }

    getUsersNoOfQuestionsOnActiveConsent(userId: string): Observable<number> {
        return this.httpClient.get<number>(this.controllerUrl + '/consenting-active/question-number'.concat(`/${userId}`));
    }

    mergeUsersActiveConsentComments(userId: string): Observable<void> {
        return this.httpClient.post<void>(this.controllerUrl + '/consenting-active/merge-comments'.concat(`/${userId}`), null);
    }

}
